import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ComplexSearch, FilterType, Order, Search, Survey, SurveyPagedApiResponse, SurveyResponse, SurveyResponsePagedApiResponse, SurveyResponsesService, SurveyTemplate, SurveyTemplatePagedApiResponse, SurveyTemplatesService, SurveysGetItemsRequestParams, SurveysService } from '../handweave-api';
import { ApiConfig } from '../shared/api-config/api-config';
import { AuthService } from '../shared/authentication/auth.service';
import { wildcardSearch } from '../shared/model/api-helper';
import { createObjectCsvStringifier } from 'csv-writer';
import { ObjectHeaderItem } from 'csv-writer/src/lib/record';

const MISSING_TENANT_ID = 'missing tenantId';
const MISSING_SURVEY_ID = 'missing surveyId';
const MISSING_TEMPLATE_ID = 'missing templateId';
const MISSING_CREDENTIAL_ID = 'missing credentialId';

@Injectable({
  providedIn: 'root',
})
export class SurveysManagerService {
  

  constructor(
    private surveysApi: SurveysService,
    private templatesApi: SurveyTemplatesService,
    private responsesApi: SurveyResponsesService,
    private authService: AuthService,
  ) { }

  listSurveys(page: number = 1, pageSize: number = 25, includeClosed: boolean = false): Observable<SurveyPagedApiResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!page) page = 1;
    const request: SurveysGetItemsRequestParams = {
      tenantId: tenantId,
      page: page,
      pageSize: pageSize,
      sortField: 'dateCreated',
      sortOrder: Order.Desc,
    }
    
    return includeClosed ? this.surveysApi.surveysGetAllItems(request) : this.surveysApi.surveysGetItems(request);
  }

  searchSurveys(query: string, page: number = 1, pageSize: number = 25): Observable<SurveyPagedApiResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!query) return this.listSurveys(page, pageSize);
    const search = wildcardSearch('name', query);
    search.sort = [ { field: 'dateCreated', sortOrder: Order.Desc }];
    const request = { 
      tenantId: tenantId, page: page, pageSize: pageSize, 
      search: search,
    };
    return this.surveysApi.surveysGetBySearch(request);
  }

  getSurvey(id: string): Observable<Survey> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    return this.surveysApi.surveysGetById({ tenantId: tenantId, id: id });
  }

  saveSurvey(model: Survey): Observable<Survey> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!model) return throwError(() => new Error('missing survey model'));
    const request = { tenantId: tenantId, survey: model, id: model.id, waitUntilIndexed: true, };
    if (model.id) {
      return this.surveysApi.surveysUpdate(request).pipe(map(_res => model));
    } else {
      return this.surveysApi.surveysCreate(request);
    }
  }

  deleteSurvey(surveyId: string): Observable<unknown> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!surveyId) return throwError(() => new Error(MISSING_SURVEY_ID));
    return this.surveysApi.surveysDelete({ tenantId: tenantId, id: surveyId, waitUntilIndexed: true });
  }

  closeSurvey(surveyId: string): Observable<unknown> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!surveyId) return throwError(() => new Error(MISSING_SURVEY_ID));
    return this.surveysApi.surveysClose({ tenantId: tenantId, id: surveyId, waitUntilIndexed: true });
  }

  openSurvey(surveyId: string): Observable<unknown> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!surveyId) return throwError(() => new Error(MISSING_SURVEY_ID));
    return this.surveysApi.surveysOpen({ tenantId: tenantId, id: surveyId, waitUntilIndexed: true });
  }

  listTemplates(page: number = 1, pageSize: number = 25): Observable<SurveyTemplatePagedApiResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!page) page = 1;
    return this.templatesApi.surveyTemplatesGetItems({ tenantId: tenantId, page: page, pageSize: pageSize, sortField: 'dateCreated', sortOrder: Order.Desc });
  }

  searchTemplates(query: string, page: number = 1, pageSize: number = 25): Observable<SurveyTemplatePagedApiResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!query) return this.listTemplates(page, pageSize);
    const search = wildcardSearch('name', query);
    search.sort = [ { field: 'dateCreated', sortOrder: Order.Desc }];
    const request = { 
      tenantId: tenantId, page: page, pageSize: pageSize, 
      search: search,
    };
    return this.templatesApi.surveyTemplatesGetBySearch(request);
  }

  getTemplate(id: string): Observable<SurveyTemplate> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    return this.templatesApi.surveyTemplatesGetById({ tenantId: tenantId, id: id });
  }

  saveTemplate(model: SurveyTemplate): Observable<SurveyTemplate> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!model) return throwError(() => new Error('missing template model'));
    const request = { tenantId: tenantId, surveyTemplate: model, id: model.id, waitUntilIndexed: true, };
    if (model.id) {
      return this.templatesApi.surveyTemplatesUpdate(request).pipe(map(_res => model));
    } else {
      return this.templatesApi.surveyTemplatesCreate(request);
    }
  }

  deleteTemplate(templateId: string): Observable<unknown> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!templateId) return throwError(() => new Error(MISSING_TEMPLATE_ID));
    return this.templatesApi.surveyTemplatesDelete({ tenantId: tenantId, id: templateId, waitUntilIndexed: true });
  }

  activateTemplate(templateId: string): Observable<Survey> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!templateId) return throwError(() => new Error(MISSING_TEMPLATE_ID));
    return this.templatesApi.surveyTemplatesActivate({ tenantId: tenantId, id: templateId, waitUntilIndexed: true, });
  }

  listResponses(surveyId: string, relatedKey: string = null, relatedValue: string = null, query: string = null, page: number = 1, pageSize: number = 25): Observable<SurveyResponsePagedApiResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!surveyId) return throwError(() => new Error(MISSING_SURVEY_ID));
    if (!page) page = 1;
    const search: Search = { sort: [ { field: 'dateCreated', sortOrder: Order.Desc } ] };
    if (!!relatedKey && !!relatedValue) {
      const complex: ComplexSearch = search.searches?.find(_ => true ) ?? { };
      complex.oneOf = complex.oneOf ?? [];
      complex.oneOf.push({ field: 'attributes.' + relatedKey, value: relatedValue, filterType: FilterType.Term });
      search.searches = [complex];
    }
    if (query) {
      const complex: ComplexSearch = search.searches?.find(_ => true ) ?? { };
      complex.oneOf = complex.oneOf ?? [];
      complex.oneOf.push({ fields: ['name'], value: `*${query}*`, filterType: FilterType.QueryString });
      search.searches = [complex];
    }
    return this.responsesApi.surveyResponsesGetBySearch({ tenantId: tenantId, page: page, pageSize: pageSize, surveyId: surveyId, search: search })
  }

  getResponse(surveyId: string, id: string): Observable<SurveyResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    return this.responsesApi.surveyResponsesGetById({ tenantId: tenantId, surveyId: surveyId, id: id });
  }

  listMyResponses(surveyId: string, page: number = 1, pageSize: number = 25): Observable<SurveyResponsePagedApiResponse> {
    const tenantId = ApiConfig.tenantId;
    const credentialId = this.authService.user.snapshot?.identity?.id?.toString();
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!credentialId) return throwError(() => new Error(MISSING_CREDENTIAL_ID));
    const search: Search = { searches: [ { oneOf: [ { field: 'changeSummary.dateCreated.userContext.userId', value: credentialId, filterType: FilterType.Term } ] } ]};
    search.sort = [ { field: 'dateCreated', sortOrder: Order.Desc } ];
    return this.responsesApi.surveyResponsesGetBySearch({ tenantId: tenantId, page: page, pageSize: pageSize, surveyId: surveyId, search: search });
  }

  getRelatedResponseId(surveyId: string, relatedKey: string, relatedValue: string): Observable<string> {
    const tenantId = ApiConfig.tenantId;
    const credentialId = this.authService.user.snapshot?.identity?.id?.toString();
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!credentialId) return throwError(() => new Error(MISSING_CREDENTIAL_ID));
    if (!relatedKey || !relatedValue) return throwError(() => new Error('missing related key or value'));
    const search: Search = { searches: [ { oneOf: [ 
      { field: 'changeSummary.dateCreated.userContext.userId', value: credentialId, filterType: FilterType.Term },
      { field: 'attributes.' + relatedKey, value: relatedValue, filterType: FilterType.Term },
    ] } ] };
    search.sort = [ { field: 'dateCreated', sortOrder: Order.Desc } ];
    return this.responsesApi.surveyResponsesGetBySearch({ tenantId: tenantId, page: 1, pageSize: 1, surveyId: surveyId, search: search }).pipe(map(res => {
      if (!res?.items?.length) return null;
      return res.items[0].id;
    }));
  }

  saveResponse(model: SurveyResponse): Observable<SurveyResponse> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!model) return throwError(() => new Error('missing response model'));
    if (!model.name) model.name = this.getResponderName();
    const request = { tenantId: tenantId, surveyResponse: model, surveyId: model.surveyId, id: model.id, waitUntilIndexed: true, };
    if (model.id) {
      return this.responsesApi.surveyResponsesUpdate(request).pipe(map(_res => model));
    } else {
      return this.responsesApi.surveyResponsesCreate(request);
    }
  }

  deleteResponse(surveyId: string, id: string): Observable<unknown> {
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!id) return throwError(() => new Error('missing responseId'));
    return this.responsesApi.surveyResponsesDelete({ tenantId: tenantId, surveyId: surveyId, id: id });
  }

  exportResponses(surveyId: string): Observable<string> {
    const pageSize = 1000; // use a large page size to reduce the number of requests
    const tenantId = ApiConfig.tenantId;
    if (!tenantId) return throwError(() => new Error(MISSING_TENANT_ID));
    if (!surveyId) return throwError(() => new Error(MISSING_SURVEY_ID));

    return this.getSurvey(surveyId).pipe(
      switchMap((survey) => this.surveyToCsv(survey, surveyId, tenantId, pageSize))
    );
  }

  public surveyToCsv(survey: Survey, surveyId: string, tenantId: string, pageSize: number): Observable<string> {
    const surveyData = JSON.parse(survey.json);
    const questionHeaders = this.extractQuestionsHeaders(surveyData);

    // Add additional columns for metadata
    const metadataHeaders : ObjectHeaderItem[] = [
      { id: 'id', title: 'id' },
      { id: 'createdOn', title: 'createdOn' },
      { id: 'createdByName', title: 'createdByName' },
      { id: 'createdByUserId', title: 'createdByUserId' },
    ];

    const search: Search = { sort: [{ field: 'dateCreated', sortOrder: Order.Desc }] };

    return this.fetchAllResponses(surveyId, tenantId, pageSize, search).pipe(
      map((responses) => this.toCsv([...metadataHeaders, ...questionHeaders], responses))
    );
  }

  private fetchResponses(surveyId: string, tenantId: string, pageSize: number, search: Search, page: number): Observable<SurveyResponsePagedApiResponse> {
    return this.responsesApi.surveyResponsesGetBySearch({
        tenantId: tenantId,
        page: page,
        pageSize: pageSize,
        surveyId: surveyId,
        search: search
    });
  }
  
  private fetchAllResponses(surveyId: string, tenantId: string, pageSize: number, search: Search, page: number = 1, responses: any[] = []): Observable<any[]> {
    return this.fetchResponses(surveyId, tenantId, pageSize, search, page).pipe(
      switchMap((res) => {

        for(const item of res.items) {  
          try {
            responses.push({
              ...JSON.parse(item.json), // Use the spread operator to map question responses
              id: item.id,
              createdOn: item.dateCreated,
              createdByName: item.changeSummary?.dateCreated?.userContext?.username,
              createdByUserId: item.changeSummary?.dateCreated?.userContext?.userId
            });
          } catch (e) {
            console.error(`Failed to parse response. ${e}\r\n${item.json}`);
          }
        }

        if (res.items.length === pageSize) {
          // Use recursion to fetch all responses
          return this.fetchAllResponses(surveyId, tenantId, pageSize, search, page + 1, responses);
        } else {
          // Return the final list of responses
          return new Observable<any[]>((observer) => {
            observer.next(responses);
            observer.complete();
          });
        }
      })
    );
  }

  public extractQuestionsHeaders(surveyData: any): ObjectHeaderItem[] {
    return surveyData.pages[0].elements.map((element: any) => ({
        id: element.name,
        title: element.name
    }));
  }

  public toCsv(header: ObjectHeaderItem[], responses: any[]): string {
    const csvStringifier = createObjectCsvStringifier({
      header: header,
    });
    let csv = csvStringifier.getHeaderString();
    csv += csvStringifier.stringifyRecords(responses);
    return csv;
  }

  private getResponderName(): string | null {
    const name = this.authService.user?.snapshot?.name;
    let builder: string = '';
    if (name?.givenName) builder += (builder.length ? ' ' + name.givenName : name.givenName);
    if (name?.familyName) builder += (builder.length ? ' ' + name.familyName : name.familyName);
    return builder || null;
  }
}
